/**
 * sap-pricing.js
 * Upload this to Shopify Theme → Assets
 * Include it in theme.liquid before </body>:
 *   {{ 'sap-pricing.js' | asset_url | script_tag }}
 *
 * Requires window.AGTHIA to be set synchronously by theme.liquid with:
 * {
 *   appBaseUrl:  string,   — your Express app base URL
 *   shopDomain:  string,   — e.g. agthiapilot.myshopify.com
 *   customerId:  string,   — Shopify customer ID (from Liquid {{ customer.id }})
 * }
 *
 * ─── DEBUG LOGGING ────────────────────────────────────────────────────────────
 * All logs are controlled by a single flag: window.AGTHIA_DEBUG
 *
 * To enable logs, run in browser console:
 *   window.AGTHIA_DEBUG = true
 *
 * Or set it permanently in theme.liquid before this script loads:
 *   window.AGTHIA = { ..., debug: true }
 *
 * To silence all logs:
 *   window.AGTHIA_DEBUG = false
 * ─────────────────────────────────────────────────────────────────────────────
 */

(function () {
    'use strict';

    const AGTHIA = window.AGTHIA || {};
    const APP_URL = AGTHIA.appBaseUrl || '';
    const SHOP = AGTHIA.shopDomain || '';
    const CUSTOMER_ID = AGTHIA.customerId || '';

    const PRICING_URL = APP_URL ? `${APP_URL}/storefront/${SHOP}/pricing/lookup` : null;
    const CUSTOMER_URL = APP_URL && SHOP && CUSTOMER_ID
        ? `${APP_URL}/storefront/${SHOP}/customer/b2b-info`
        : null;

    const FETCH_TIMEOUT_MS = 5000;

    // ─── Logger ────────────────────────────────────────────────────────────────

    const PREFIX = '[SAP-Pricing]';

    function isDebug() {
        return window.AGTHIA_DEBUG === true || window.AGTHIA?.debug === true;
    }

    const log = {
        info: (...args) => { if (isDebug()) console.log(PREFIX, ...args); },
        warn: (...args) => { if (isDebug()) console.warn(PREFIX, ...args); },
        error: (...args) => { if (isDebug()) console.error(PREFIX, ...args); },
        group: (label) => { if (isDebug()) console.group(`${PREFIX} ${label}`); },
        groupEnd: () => { if (isDebug()) console.groupEnd(); },
        table: (data) => { if (isDebug()) console.table(data); },
    };

    // ─── Step 1: Fetch B2B customer context ───────────────────────────────────

    async function fetchCustomerContext() {
        log.group('fetchCustomerContext');

        if (AGTHIA.customer?.success !== undefined) {
            log.info('Customer context already in window.AGTHIA.customer — reusing', AGTHIA.customer);
            log.groupEnd();
            return AGTHIA.customer;
        }

        if (!CUSTOMER_URL) {
            log.warn('Cannot fetch customer context — missing one of: appBaseUrl, shopDomain, customerId', {
                appBaseUrl: APP_URL || '(missing)',
                shopDomain: SHOP || '(missing)',
                customerId: CUSTOMER_ID || '(missing)',
            });
            log.groupEnd();
            return null;
        }

        log.info('Fetching customer context from:', CUSTOMER_URL, '| customerId:', CUSTOMER_ID);

        try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

            const res = await fetch(CUSTOMER_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ customerId: CUSTOMER_ID }),
                signal: controller.signal,
            });

            clearTimeout(timeout);
            log.info('Customer context HTTP response:', res.status, res.statusText);

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const data = await res.json();
            log.info('Customer context response data:', data);

            AGTHIA.customer = data;
            log.groupEnd();
            return data;

        } catch (err) {
            if (err.name === 'AbortError') {
                log.error('Customer context fetch TIMED OUT after', FETCH_TIMEOUT_MS, 'ms');
            } else {
                log.error('Customer context fetch FAILED:', err.message, err);
            }
            log.groupEnd();
            return null;
        }
    }

    // ─── Step 2: Build flat context object ────────────────────────────────────

    function buildContext(customerData) {
        log.group('buildContext');

        if (!customerData) {
            log.warn('No customer data — skipping B2B pricing');
            log.groupEnd();
            return null;
        }

        if (!customerData.success) {
            log.warn('Customer data returned success=false — not a valid B2B customer', customerData);
            log.groupEnd();
            return null;
        }

        if (!customerData.isB2b) {
            log.info('Customer is not B2B (isB2b=false) — Shopify prices will show normally');
            log.groupEnd();
            return null;
        }

        const customer = customerData.customer;
        const company = customerData.company;

        if (!customer || !company) {
            log.warn('Missing customer or company object in context', { customer, company });
            log.groupEnd();
            return null;
        }

        const sapCompany = company.sapData || {};

        const ctx = {
            customerId: customer.id,
            salesOrderBlock: sapCompany.salesOrderBlock === 'true',
            deliveryBlock: sapCompany.deliveryBlock === 'true',
            overallBlock: sapCompany.overallBlock === 'true',
            sapCustomerId: company.externalId || '',
            companyId: company.id || '',
            companyName: company.name || '',
            salesOrg: sapCompany.salesOrg || '',
            distChannel: sapCompany.distributionChannel || '',
            division: sapCompany.division || '',
            customerChannel: sapCompany.customerChannel || '',
            priceGroup: sapCompany.customerPriceGroup || '',
            currency: sapCompany.currency || 'AED',
            deliveryPlant: sapCompany.deliveryPlant || '',
        };

        log.info('Built B2B context:', ctx);

        if (!ctx.sapCustomerId) {
            log.warn('sapCustomerId (company.externalId) is empty — pricing lookup will fail');
        }

        log.groupEnd();
        return ctx;
    }

    function isBlocked(ctx) {
        return ctx.overallBlock === true || ctx.salesOrderBlock === true || ctx.deliveryBlock === true;
    }

    // ─── Utilities ─────────────────────────────────────────────────────────────

    function formatPrice(amount, currency) {
        try {
            return new Intl.NumberFormat('en-AE', {
                style: 'currency',
                currency: currency || 'AED',
                minimumFractionDigits: 2,
            }).format(amount);
        } catch (e) {
            return `${currency || 'AED'} ${parseFloat(amount).toFixed(2)}`;
        }
    }

    function buildLookupItem(ref, sku, ctx) {
        return {
            ref,
            material: sku,
            customer: ctx.sapCustomerId,
            salesOrg: ctx.salesOrg,
            distChannel: ctx.distChannel,
            customerPriceGroup: ctx.priceGroup,
            channel: ctx.customerChannel,
        };
    }

    // ─── DOM helpers ───────────────────────────────────────────────────────────

    function getPriceWrappers() {
        return document.querySelectorAll('[data-sap-price-wrapper]');
    }

    // Returns the wrapper scoped to the main product form only (not recommendations).
    // This is the wrapper that controls the ATC button.
    function getMainProductWrapper() {
        // Prefer a wrapper inside the product form or product-information section
        const productSection = document.querySelector(
            '#ProductInfo-' + (document.querySelector('[id^="ProductInfo-"]')?.id || '') +
            ', .product-information--inner, .product-information, [data-section-type="product"]'
        );
        if (productSection) {
            const wrapper = productSection.querySelector('[data-sap-price-wrapper]');
            if (wrapper) return wrapper;
        }
        // Fallback: first wrapper on the page (which is always the main product, index 0)
        return document.querySelector('[data-sap-price-wrapper]');
    }

    function hideAtcButtons() {
        document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
            btn.style.visibility = 'hidden';
        });
    }

    function showAtcButtons() {
        document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
            btn.style.visibility = 'visible';
        });
    }

    function disableAtcButtons() {
        document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
            btn.style.display = 'none';
            btn.disabled = true;
        });
    }

    function hidePriceElements() {
        const els = getPriceWrappers();
        log.info(`Hiding ${els.length} price wrapper(s)`);
        els.forEach(el => { el.style.visibility = 'hidden'; });
    }

    // ─── injectPrice ──────────────────────────────────────────────────────────
    // NOTE: does NOT call showAtcButtons() — ATC visibility is controlled
    //       by the caller (initProductPage / variant:change) based on the
    //       main product wrapper result only, not recommendation wrappers.

    function injectPrice(wrapper, priceData) {
        const sku = wrapper.dataset.sapSku || '(no sku)';
        const insEl = wrapper.querySelector('ins .amount, ins span.amount');
        const delEl = wrapper.querySelector('del');

        if (!priceData.available) {
            log.warn(`SKU ${sku}: price not available — showing "Contact us"`, priceData);
            const ins = wrapper.querySelector('ins');
            if (ins) {
                const contactText = window.AGTHIA?.strings?.contactForPricing || 'Contact us for pricing';
                ins.innerHTML = `<span class="amount sap-unavailable" style="font-size:0.85em;color:#999;">${contactText}</span>`;
            }
            if (delEl) delEl.style.display = 'none';
            wrapper.style.visibility = 'visible';
            return;
        }

        const formatted = formatPrice(priceData.unitPrice, priceData.currency);
        log.info(`SKU ${sku}: injecting price ${formatted}`, priceData);

        if (insEl) {
            log.info(`SKU ${sku}: found <ins .amount> element, replacing text`);
            insEl.textContent = formatted;
        } else {
            log.warn(`SKU ${sku}: could not find <ins .amount> or <ins span.amount> — price not displayed`, wrapper);
        }

        if (delEl) delEl.style.display = 'none';

        if (priceData.conditionUnit && !wrapper.querySelector('.sap-unit-badge')) {
            const badge = document.createElement('span');
            badge.className = 'sap-unit-badge';
            badge.style.cssText = 'font-size:0.75em;color:#666;margin-left:4px;display:inline-block;';
            badge.textContent = `/ ${priceData.conditionUnit}`;
            const ins = wrapper.querySelector('ins');
            if (ins) ins.appendChild(badge);
        }

        wrapper.dataset.sapPrice = priceData.unitPrice;
        wrapper.dataset.sapCurrency = priceData.currency;
        wrapper.dataset.sapConditionRecord = priceData.conditionRecord || '';
        wrapper.dataset.sapConditionUnit = priceData.conditionUnit || '';
        wrapper.dataset.sapWaterfallLevel = priceData.waterfallLevel || '';
        wrapper.dataset.sapDateFrom = priceData.dateFrom || '';
        wrapper.dataset.sapDateTo = priceData.dateTo || '';

        wrapper.style.visibility = 'visible';

        // ← showAtcButtons() intentionally removed here.
        //   ATC is controlled by initProductPage / variant:change based on
        //   the MAIN product wrapper result only.
    }

    function showBlockedState() {
        log.warn('Customer is blocked — hiding ATC buttons and showing blocked message');
        document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
            btn.style.display = 'none';
        });
        document.querySelectorAll('[data-sap-atc-wrapper]').forEach(wrapper => {
            if (!wrapper.querySelector('.sap-blocked-msg')) {
                const msg = document.createElement('p');
                msg.className = 'sap-blocked-msg';
                msg.style.cssText = 'color:#c00;font-size:0.9em;margin:8px 0 0;';
                msg.textContent = 'Ordering is currently unavailable for your account. Please contact your account manager.';
                wrapper.appendChild(msg);
            }
        });
    }

    function showPriceUnavailable() {
        log.info('showPriceUnavailable: replacing all prices for non-B2B visitor');

        getPriceWrappers().forEach(wrapper => {
            const ins = wrapper.querySelector('ins');
            const del = wrapper.querySelector('del');
            if (ins) {
                ins.innerHTML = '<span class="amount sap-unavailable" style="font-size:0.85em;color:#999;">Price not available</span>';
            }
            if (del) del.style.display = 'none';
            wrapper.style.visibility = 'visible';
        });

        document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
            btn.disabled = true;
            btn.textContent = 'Not available';
        });
    }

    // ─── Pricing API ───────────────────────────────────────────────────────────

    async function fetchPrices(items) {
        if (!PRICING_URL) {
            log.warn('fetchPrices: PRICING_URL is not set — returning unavailable for all items');
            return items.map(i => ({ ref: i.ref, available: false }));
        }

        log.group(`fetchPrices — ${items.length} item(s)`);
        log.info('POST', PRICING_URL);
        log.table(items.map(i => ({
            ref: i.ref,
            material: i.material,
            customer: i.customer,
            salesOrg: i.salesOrg,
            distChannel: i.distChannel,
            customerPriceGroup: i.customerPriceGroup,
            channel: i.channel,
        })));

        try {
            const res = await fetch(PRICING_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(items),
            });

            log.info('Pricing API HTTP response:', res.status, res.statusText);

            if (!res.ok) throw new Error(`HTTP ${res.status}`);

            const results = await res.json();
            log.info('Pricing API results:');
            log.table(results.map(r => ({
                ref: r.ref,
                material: r.material || '?',
                available: r.available,
                unitPrice: r.unitPrice,
                currency: r.currency,
                waterfallLevel: r.waterfallLevel,
            })));
            log.groupEnd();
            return results;

        } catch (err) {
            log.error('fetchPrices FAILED:', err.message, err);
            log.groupEnd();
            return items.map(i => ({ ref: i.ref, available: false }));
        }
    }

    // ─── Product page ──────────────────────────────────────────────────────────

    async function initProductPage(ctx) {
        log.group('initProductPage');

        const blocked = isBlocked(ctx);

        if (blocked) {
            log.warn('Customer is blocked — pricing will still load, but ATC disabled');
            showBlockedState();
        }

        const wrappers = getPriceWrappers();
        log.info(`Found ${wrappers.length} [data-sap-price-wrapper] element(s) on product page`);

        if (wrappers.length === 0) {
            log.warn('No price wrappers found — check that your price snippet has data-sap-price-wrapper and data-sap-sku attributes');
            log.groupEnd();
            return;
        }

        const items = [];
        wrappers.forEach((wrapper, i) => {
            const sku = wrapper.dataset.sapSku;
            if (sku) {
                log.info(`Wrapper ${i}: SKU = "${sku}"`);
                items.push(buildLookupItem(`wrapper-${i}`, sku, ctx));
                wrapper.dataset.sapRef = `wrapper-${i}`;
            } else {
                log.warn(`Wrapper ${i}: data-sap-sku is empty or missing — skipping`, wrapper);
            }
        });

        if (items.length === 0) {
            log.warn('No items with a valid SKU — nothing to price');
            log.groupEnd();
            return;
        }

        hideAtcButtons();
        hidePriceElements();

        const results = await fetchPrices(items);
        const resultMap = {};
        results.forEach(r => { resultMap[r.ref] = r; });

        wrappers.forEach((wrapper, i) => {
            const result = resultMap[`wrapper-${i}`];
            if (result) {
                injectPrice(wrapper, result);
            } else {
                log.warn(`Wrapper ${i}: no result returned from API — showing original price`);
                wrapper.style.visibility = 'visible';
            }
        });

        // ─── ATC visibility: controlled by main product (wrapper-0) ONLY ───────
        // Recommendation wrappers (1, 2, 3, 4...) must not affect the ATC button.
        const mainResult = resultMap['wrapper-0'];
        if (blocked) {
            // already handled by showBlockedState() above
            log.info('ATC hidden due to customer block');
        } else if (mainResult?.available) {
            log.info('Main product available — showing ATC button');
            showAtcButtons();
        } else {
            log.warn('Main product unavailable — hiding ATC button');
            disableAtcButtons();
        }

        log.groupEnd();
    }

    // ─── Variant change ────────────────────────────────────────────────────────

    function initVariantChangeListener(ctx) {
        log.info('initVariantChangeListener: listening for variant:change events');

        document.addEventListener('variant:change', async function (e) {
            const variant = e.detail?.variant;
            log.group('variant:change');
            log.info('Variant changed:', variant);

            if (!variant?.sku) {
                log.warn('variant:change fired but variant has no SKU — skipping', variant);
                log.groupEnd();
                return;
            }

            // Only update the MAIN product wrapper (wrapper-0), not recommendations
            const mainWrapper = getMainProductWrapper();
            if (!mainWrapper) {
                log.warn('No main product wrapper found on variant change');
                log.groupEnd();
                return;
            }

            mainWrapper.dataset.sapSku = variant.sku;

            hideAtcButtons();
            mainWrapper.style.visibility = 'hidden';

            const results = await fetchPrices([
                buildLookupItem('variant-change', variant.sku, ctx)
            ]);

            const result = results[0];
            injectPrice(mainWrapper, result || { available: false });

            // ATC controlled by this variant's result only
            document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
                if (result?.available === false) {
                    btn.disabled = true;
                    btn.textContent = btn.dataset.originalText || 'Add to cart';
                    btn.style.display = 'none';
                } else {
                    btn.disabled = false;
                    btn.style.display = '';
                    btn.style.visibility = 'visible';
                    btn.textContent = btn.dataset.originalText || 'Add to cart';
                }
            });

            log.groupEnd();
        });

        document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
            btn.dataset.originalText = btn.textContent.trim();
        });
    }

    // ─── Collection page ───────────────────────────────────────────────────────
    // Also used for recommendation sections on product/cart pages.
    // MutationObserver watches for infinite scroll cards injected after load.

    function initCollectionPage(ctx) {
        log.group('initCollectionPage');

        if (isBlocked(ctx)) {
            log.warn('Customer is blocked — ATC buttons will be hidden on collection page');
            function hideAllAtcButtons() {
                document.querySelectorAll('[data-sap-atc-btn]').forEach(btn => {
                    btn.style.display = 'none';
                });
            }
            hideAllAtcButtons();
            const atcObserver = new MutationObserver(hideAllAtcButtons);
            atcObserver.observe(document.body, { childList: true, subtree: true });
        }

        const BATCH_SIZE = 50;
        const DEBOUNCE_MS = 120;
        let debounceTimer = null;

        async function priceWrappers(wrappers) {
            if (wrappers.length === 0) return;

            log.group(`priceWrappers — ${wrappers.length} new wrapper(s)`);

            const items = [];
            wrappers.forEach((wrapper, i) => {
                const sku = wrapper.dataset.sapSku;
                if (sku) {
                    const ref = `col-${Date.now()}-${i}`;
                    wrapper.dataset.sapQueued = '1';
                    wrapper.style.visibility = 'hidden';
                    items.push(buildLookupItem(ref, sku, ctx));
                    wrapper.dataset.sapRef = ref;
                    log.info(`Queued wrapper ${i}: SKU="${sku}" ref="${ref}"`);
                } else {
                    log.warn(`Wrapper ${i}: data-sap-sku missing — skipping`, wrapper);
                }
            });

            if (items.length === 0) {
                log.warn('No valid SKUs in this batch — nothing sent to API');
                log.groupEnd();
                return;
            }

            const results = [];
            for (let i = 0; i < items.length; i += BATCH_SIZE) {
                const batch = items.slice(i, i + BATCH_SIZE);
                log.info(`Fetching batch ${Math.floor(i / BATCH_SIZE) + 1}: ${batch.length} item(s)`);
                results.push(...await fetchPrices(batch));
            }

            const resultMap = {};
            results.forEach(r => { resultMap[r.ref] = r; });

            wrappers.forEach(wrapper => {
                const ref = wrapper.dataset.sapRef;
                const result = ref ? resultMap[ref] : null;
                if (result) {
                    injectPrice(wrapper, result);
                } else {
                    log.warn(`No result for ref "${ref}" — showing original price`, wrapper);
                    wrapper.style.visibility = 'visible';
                }
            });

            log.groupEnd();
        }

        function getUnpricedWrappers() {
            return Array.from(
                document.querySelectorAll('[data-sap-price-wrapper]:not([data-sap-queued])')
            );
        }

        const initial = getUnpricedWrappers();
        log.info(`Initial load: found ${initial.length} unpriced wrapper(s)`);
        priceWrappers(initial);

        const observer = new MutationObserver(() => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                const fresh = getUnpricedWrappers();
                if (fresh.length > 0) {
                    log.info(`MutationObserver: detected ${fresh.length} new wrapper(s) from infinite scroll`);
                    priceWrappers(fresh);
                }
            }, DEBOUNCE_MS);
        });

        observer.observe(document.body, { childList: true, subtree: true });
        log.info('MutationObserver active — watching for infinite scroll additions');
        log.groupEnd();
    }

    // ─── Cart attributes ───────────────────────────────────────────────────────

    function initCartAttributes(ctx) {
        log.info('initCartAttributes: intercepting cart/add events');

        document.addEventListener('submit', function (e) {
            const form = e.target;
            if (!(form instanceof HTMLFormElement)) return;
            if (!form.matches('form[action*="/cart/add"], form[action*="/cart/add.js"], form[data-product-form]')) return;

            log.info('Form submit intercepted — injecting SAP properties', form);
            injectSapPropertiesIntoForm(form, ctx);
        }, true);

        const originalFetch = window.fetch;
        window.fetch = async function (input, options = {}) {
            const requestUrl =
                typeof input === 'string'
                    ? input
                    : input && typeof input.url === 'string'
                        ? input.url
                        : '';

            if (requestUrl.includes('/cart/add')) {
                log.info('fetch /cart/add intercepted — injecting SAP properties', requestUrl);
                options = injectSapPropertiesIntoFetch(input, options, ctx);
            }

            const response = await originalFetch.call(this, input, options);

            // Re-run SAP cart pricing after any quantity change
            if (requestUrl.includes('/cart/change') || requestUrl.includes('/cart/update')) {
                log.info('fetch /cart/change or /cart/update detected — re-running initCartPage');
                response.clone().json().then(() => {
                    initCartPage(ctx);
                }).catch(() => {
                    initCartPage(ctx);
                });
            }

            return response;
        };
    }

    function getCurrentVariantSku() {
        const selectedVariantInput = document.querySelector('form[action*="/cart/add"] [name="id"], form[data-product-form] [name="id"]');
        const selectedVariantId = selectedVariantInput ? String(selectedVariantInput.value || '') : '';

        const candidates = Array.from(document.querySelectorAll('[data-sap-price-wrapper][data-sap-price]'));
        if (candidates.length === 0) {
            log.warn('getSapContextFromPage: no priced wrapper found');
            return null;
        }

        const variantMatch =
            candidates.find((wrapper) => String(wrapper.dataset.variantId || '') === selectedVariantId) ||
            candidates.find((wrapper) => wrapper.dataset.selected === 'true') ||
            candidates[0];

        return variantMatch;
    }

    function getSapContextFromPage() {
        const wrapper = getCurrentVariantSku();
        if (!wrapper) return null;

        const sapPrice = wrapper.dataset.sapPrice || '';
        if (!sapPrice) {
            log.warn('getSapContextFromPage: wrapper found but no data-sap-price present', wrapper);
            return null;
        }

        return {
            _sapPrice: wrapper.dataset.sapPrice || '',
            _sapCurrency: wrapper.dataset.sapCurrency || '',
            _sapConditionRecord: wrapper.dataset.sapConditionRecord || '',
            _sapConditionUnit: wrapper.dataset.sapConditionUnit || '',
            _sapWaterfallLevel: wrapper.dataset.sapWaterfallLevel || '',
            _sapSku: wrapper.dataset.sapSku || '',
        };
    }

    function buildSapProperties(ctx) {
        const sap = getSapContextFromPage();
        if (!sap) return null;

        return {
            ...sap,
            _sapCustomerId: ctx.sapCustomerId || '',
            _sapSalesOrg: ctx.salesOrg || '',
            _sapDistCh: ctx.distChannel || '',
            _sapPriceGroup: ctx.priceGroup || '',
            _sapChannel: ctx.customerChannel || '',
            _sapPlant: ctx.deliveryPlant || '',
        };
    }

    function injectSapPropertiesIntoForm(form, ctx) {
        const props = buildSapProperties(ctx);
        if (!props) return;

        log.info('Injecting SAP properties into form:', props);

        Object.entries(props).forEach(([key, value]) => {
            let input = form.querySelector(`[name="properties[${key}]"]`);
            if (!input) {
                input = document.createElement('input');
                input.type = 'hidden';
                input.name = `properties[${key}]`;
                form.appendChild(input);
            }
            input.value = value;
        });
    }

    function injectSapPropertiesIntoFetch(input, options, ctx) {
        const props = buildSapProperties(ctx);
        if (!props) return options;

        const nextOptions = { ...options };

        if (nextOptions.body instanceof FormData) {
            Object.entries(props).forEach(([key, value]) => {
                nextOptions.body.set(`properties[${key}]`, value);
            });
            log.info('Injected SAP properties into FormData body:', props);
            return nextOptions;
        }

        if (typeof nextOptions.body === 'string') {
            const trimmed = nextOptions.body.trim();

            try {
                const parsed = JSON.parse(trimmed);
                parsed.properties = {
                    ...(parsed.properties || {}),
                    ...props,
                };
                nextOptions.body = JSON.stringify(parsed);
                log.info('Injected SAP properties into JSON body:', parsed.properties);
                return nextOptions;
            } catch (err) {
                log.warn('Fetch body is not JSON; trying URLSearchParams fallback');
            }

            const params = new URLSearchParams(trimmed);
            Object.entries(props).forEach(([key, value]) => {
                params.set(`properties[${key}]`, value);
            });
            nextOptions.body = params.toString();
            log.info('Injected SAP properties into URL-encoded body:', props);
            return nextOptions;
        }

        if (!nextOptions.body && input instanceof Request) {
            log.warn('Request object body cannot be safely rewritten in this interceptor');
        }

        return nextOptions;
    }

    function hideCartPrices() {
        document.querySelector('#Cart')?.classList.add('sap-loading');
        document.querySelectorAll('td.product-subtotal .price, .cart-subtotal td .price, .cart-discount td .price').forEach(el => {
            el.style.visibility = 'hidden';
        });
    }

    function showCartPrices() {
        document.querySelector('#Cart')?.classList.remove('sap-loading');
        document.querySelectorAll('td.product-subtotal .price, .cart-subtotal td .price, .cart-discount td .price').forEach(el => {
            el.style.visibility = 'visible';
        });
    }

    // ─── Cart page ─────────────────────────────────────────────────────────────

    async function initCartPage(ctx) {
        log.group('initCartPage');
        hideCartPrices();

        async function fetchCartJson() {
            const res = await fetch('/cart.json');
            return res.json();
        }

        let cart;
        try {
            cart = await fetchCartJson();
        } catch (err) {
            log.error('initCartPage: failed to fetch /cart.json', err);
            showCartPrices(); // ← always show prices on error, never leave hidden
            log.groupEnd();
            return;
        }

        if (!cart?.token || !cart?.items?.length) {
            log.info('initCartPage: cart empty or no token');
            showCartPrices(); // ← always show prices on early return
            log.groupEnd();
            return;
        }

        const cartId = `gid://shopify/Cart/${cart.token}`;

        const lines = cart.items
            .map(item => ({
                cartLineId: item.key,
                sku: item.sku,
                quantity: String(item.quantity),
            }))
            .filter(l => l.sku);

        if (lines.length === 0) {
            log.info('initCartPage: no lines with SKU — skipping');
            showCartPrices(); // ← always show prices on early return
            log.groupEnd();
            return;
        }

        const pricesBefore = {};
        cart.items.forEach(item => { pricesBefore[item.variant_id] = item.price; });

        log.info('initCartPage: always refreshing SAP prices', { cartId, lines });

        try {
            const res = await fetch(`${APP_URL}/storefront/${SHOP}/pricing/refresh-sap-prices`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    cartId,
                    lines,
                    customerContext: {
                        sapCustomerId: ctx.sapCustomerId,
                        salesOrg: ctx.salesOrg,
                        distChannel: ctx.distChannel,
                        priceGroup: ctx.priceGroup,
                        customerChannel: ctx.customerChannel,
                    },
                }),
            });
            const data = await res.json();
            log.info('initCartPage: refresh response', data);
        } catch (err) {
            log.error('initCartPage: refresh request failed', err);
            showCartPrices(); // ← show prices even if refresh fails
            log.groupEnd();
            return;
        }

        let updatedCart;
        try {
            updatedCart = await fetchCartJson();
        } catch (err) {
            log.error('initCartPage: failed to re-fetch /cart.json after refresh', err);
            showCartPrices(); // ← show prices even if re-fetch fails
            log.groupEnd();
            return;
        }

        const itemCountChanged = updatedCart.items.length !== cart.items.length;
        const itemPriceChanged = updatedCart.items.some(item => item.price !== pricesBefore[item.variant_id]);

        log.info('initCartPage: itemCountChanged:', itemCountChanged, `(before: ${cart.items.length}, after: ${updatedCart.items.length})`);
        log.info('initCartPage: itemPriceChanged:', itemPriceChanged);

        const pricesChanged = itemCountChanged || itemPriceChanged;

        if (pricesChanged) {
            log.info('initCartPage: prices changed after refresh — reloading page');
            window.location.reload();
        } else {
            log.info('initCartPage: prices unchanged after refresh — no reload needed');
            showCartPrices();
        }

        log.groupEnd();
    }

    // ─── Main ──────────────────────────────────────────────────────────────────

    async function main() {
        log.group('SAP Pricing — main()');
        log.info('window.AGTHIA:', AGTHIA);
        log.info('Page template classes:', document.body.className);

        if (!CUSTOMER_ID) {
            log.info('No customerId — customer not logged in, showing "Price not available"');
            showPriceUnavailable();
            log.groupEnd();
            return;
        }

        log.info('Customer ID:', CUSTOMER_ID);

        const customerData = await fetchCustomerContext();
        const ctx = buildContext(customerData);

        if (!ctx?.sapCustomerId) {
            log.info('No B2B context — replacing prices with "Price not available"');
            showPriceUnavailable();
            log.groupEnd();
            return;
        }

        log.info('B2B context ready. sapCustomerId:', ctx.sapCustomerId, '| company:', ctx.companyName);
        log.info('Block flags — overallBlock:', ctx.overallBlock, '| salesOrderBlock:', ctx.salesOrderBlock, '| deliveryBlock:', ctx.deliveryBlock);

        const isProduct = document.body.classList.contains('template-product');
        const isListingPage = document.body.classList.contains('template-collection')
            || document.body.classList.contains('template-index')
            || document.body.classList.contains('template-search')
            || document.body.classList.contains('template-page');
        const isCart = document.body.classList.contains('template-cart');

        log.info('Page type — isProduct:', isProduct, '| isListingPage:', isListingPage, '| isCart:', isCart);

        if (isProduct) {
            // initProductPage handles wrapper-0 (main product) ATC visibility.
            // initCollectionPage handles recommendation wrappers (1, 2, 3, 4...).
            // They run concurrently — product page controls ATC, collection page
            // just prices the cards without touching ATC.
            initProductPage(ctx);
            initVariantChangeListener(ctx);
            // Price recommendation section wrappers that don't have data-sap-queued
            initCollectionPage(ctx);
        }

        if (isListingPage) {
            initCollectionPage(ctx);
        }

        if (isCart) {
            initCartPage(ctx);
            // Price "You may also like" recommendation wrappers on cart page
            initCollectionPage(ctx);
        }

        initCartAttributes(ctx);
        log.groupEnd();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', main);
    } else {
        main();
    }

    // ─── Safety net ────────────────────────────────────────────────────────────
    // If the API never responded within FETCH_TIMEOUT_MS + 1s, show
    // "Price not available" rather than leaving prices hidden forever.
    // Cart line item prices are excluded (they have their own flow via
    // hideCartPrices/showCartPrices), but recommendation wrappers on the
    // cart page ARE included since they go through initCollectionPage.
    setTimeout(() => {
        const stillHidden = Array.from(getPriceWrappers()).some(
            el => el.style.visibility === 'hidden'
        );
        if (stillHidden) {
            log.warn('Safety net triggered — prices still hidden after timeout, showing "Price not available"');
            showPriceUnavailable();
        }

        // Also ensure cart line prices are never permanently hidden
        const isCart = document.body.classList.contains('template-cart');
        if (isCart) {
            const cartPricesHidden = Array.from(
                document.querySelectorAll('td.product-subtotal .price, .cart-subtotal td .price')
            ).some(el => el.style.visibility === 'hidden');
            if (cartPricesHidden) {
                log.warn('Safety net: cart prices still hidden — forcing showCartPrices()');
                showCartPrices();
            }
        }
    }, FETCH_TIMEOUT_MS + 1000);

})();